import { Injectable } from '@angular/core';
import { ApiClientService } from '../api-client.service';
import { Observable } from 'rxjs/Observable';
import { ApiResponseList } from '../../types/api-response/helpers';
import { Announcement, IAnnouncement } from '../../types/website-builder/announcement';
import { catchError } from 'rxjs/operators';
import { _throw } from 'rxjs/observable/throw';
import { IAnnouncementCnSettings } from '../../types/api-response/announcements/announcementCnSettings';
import { of } from 'rxjs/observable/of';

@Injectable()
export class AnnouncementApiService {

    constructor(private api: ApiClientService) {}

    /**
     * Returns notices list
     * for selected corporation number ID
     */
    getNoticesList(params): Observable<ApiResponseList<IAnnouncement>> {
        const allowedParams = ['corporationNumberId', 'query', 'page', 'per-page', 'order-by'];

        return this.api.getJson('domainAnnouncements', `/v1/notice` + ApiClientService.serialize(params, allowedParams)).pipe(
            catchError(err => {
                this.api.errorHandler(err, 'Failed to get Announcements');
                return _throw(err);
            })
        );
    }

    /**
     * Returns notices list
     * for selected corporation number ID
     */
    getNoticesByCorporationNumberId(corporationNumberId): Observable<ApiResponseList<IAnnouncement>> {
        return this.api.getJson('domainAnnouncements', `/v1/notice/cn_${corporationNumberId}`);
    }

    /**
     * Returns notice details
     */
    getNotice(noticeId: string): Observable<IAnnouncement> {
        return this.api.getJson('domainAnnouncements', `/v1/notice/${noticeId}`);
    }

    /**
     * Create or Update notice
     * @param notice
     */
    setNotice(notice: IAnnouncement) {
        return notice.id ? this.updateNotice(notice) : this.createNotice(notice);
    }

    /**
     * Returns notice details
     */
    createNotice(notice: IAnnouncement): Observable<IAnnouncement> {
        const allowedParams: Array<keyof IAnnouncement>  = ['title', 'text', 'document', 'periodBegin', 'periodEnd', 'corporationNumberId', 'corporationNumberName'];
        return this.api.post('domainAnnouncements', '/v1/notice', ApiClientService.filter(notice, allowedParams));
    }

    /**
     * Returns notice details
     */
    updateNotice(params: Partial<IAnnouncement>): Observable<IAnnouncement> {
        const allowedParams: Array<keyof IAnnouncement>  = ['title', 'text', 'document', 'periodBegin', 'periodEnd', 'corporationNumberName', 'resendToSubscribers'];
        return this.api.patch('domainAnnouncements', `/v1/notice/${params.id}`, ApiClientService.filter(params, allowedParams));
    }

    /**
     * Returns notice details
     */
    removeNotice(noticeId: string): Observable<IAnnouncement> {
        return this.api.remove('domainAnnouncements', `/v1/notice/${noticeId}`);
    }


    /**
     * Returns announcement corporation settings list
     */
    getCorporationNumberSettingsList(): Observable<ApiResponseList<IAnnouncementCnSettings>> {
        return this.api.getJson('domainAnnouncements', `/v1/settings`);
    }

    /**
     * Returns announcement corporation settings list
     * Returns null if settings not found
     */
    getCorporationNumberSettings(corporationNumberId): Observable<IAnnouncementCnSettings|null> {
        return this.api.getJson('domainAnnouncements', `/v1/settings/${corporationNumberId}`).pipe(
            catchError((response: Response) => {
                if(response.status === 404) {
                    return of(null);
                }

                return _throw(response);
            })
        );
    }

    /**
     * Set Corporation number settings
     * @param cnSettings
     */
    setCorporationNumberSettings(cnSettings: IAnnouncementCnSettings) {
        return cnSettings.id ? this.updateCorporationNumberSettings(cnSettings) : this.createCorporationNumberSettings(cnSettings);
    }

    /**
     * Create Corporation number settings
     * @param cnSettings
     */
    createCorporationNumberSettings(cnSettings: IAnnouncementCnSettings) {
        const allowedParams: Array<keyof IAnnouncementCnSettings>  = ['corporationNumberId', 'confirmationSubject', 'corporationNumberName', 'corporationNumberWebsite', 'senderEmail'];
        return this.api.post('domainAnnouncements', `/v1/settings`, ApiClientService.filter(cnSettings, allowedParams, false));
    }

    /**
     * Update Corporation number settings
     * @param cnSettings
     */
    updateCorporationNumberSettings(cnSettings: IAnnouncementCnSettings) {
        const allowedParams: Array<keyof IAnnouncementCnSettings>  = ['confirmationSubject', 'corporationNumberName', 'corporationNumberWebsite', 'senderEmail'];
        return this.api.patch('domainAnnouncements', `/v1/settings/${cnSettings.id}`, ApiClientService.filter(cnSettings, allowedParams));
    }
}



// WEBPACK FOOTER //
// /app/src/app/services/api/announcement-api.service.ts